import { useMemo, useState } from "react";
import { Edit2, Eye, EyeOff, FileText, HelpCircle, Hash, Link2, Lock, Trash2, Unlock } from "lucide-react";
import { api, type AdminCourseQuizItem } from "../../lib/api";
import { Badge } from "../ui/Badge";
import { toast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";
import { AdminDataTable, type Column, type RowAction } from "./dataTable/AdminDataTable";
import { BulkBar, type BulkAction } from "./dataTable/BulkBar";
import { FilterBar, type FilterDef } from "./dataTable/FilterBar";
import { useListState } from "./dataTable/useListState";

type Props = {
  items: AdminCourseQuizItem[];
  token: string;
  courseId: string;
  isCoursePremium: boolean;
  isUnassigned: boolean;
  onChanged: () => void | Promise<void>;
  onEdit: (q: AdminCourseQuizItem) => void;
  onPreview: (q: AdminCourseQuizItem) => void;
  onRaw: (q: AdminCourseQuizItem) => void;
  onToggleVisibility: (q: AdminCourseQuizItem) => void | Promise<void>;
  onToggleAccess: (q: AdminCourseQuizItem) => void | Promise<void>;
  onDelete: (q: AdminCourseQuizItem) => void;
  onAdd?: () => void;
};

function statusOf(q: AdminCourseQuizItem): { label: string; variant: "success" | "warning" | "secondary" } {
  if (!q.isPublished) return { label: "Draft", variant: "warning" };
  if (q.isHidden) return { label: "Hidden", variant: "secondary" };
  return { label: "Published", variant: "success" };
}

/** Quizzes use the same table as modules and exams; only the actions the quiz endpoints support. */
export function CourseQuizTable(p: Props) {
  const list = useListState("q", ["status", "access"], { key: "date", dir: "desc" });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = list.q.trim().toLowerCase();
    return p.items.filter((i) => {
      if (q && !`${i.title} ${i.topic ?? ""}`.toLowerCase().includes(q)) return false;
      if (list.filters.status && statusOf(i).label.toLowerCase() !== list.filters.status) return false;
      if (list.filters.access === "free" && i.isFree === false) return false;
      if (list.filters.access === "premium" && i.isFree !== false) return false;
      return true;
    });
  }, [p.items, list.q, list.filters]);

  const filters: FilterDef[] = [
    { key: "status", label: "Status", options: [{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }, { value: "hidden", label: "Hidden" }] },
    { key: "access", label: "Access", options: [{ value: "free", label: "Free" }, { value: "premium", label: "Premium" }] },
  ];

  const copy = (text: string, what: string) =>
    void navigator.clipboard?.writeText(text).then(
      () => toast.success(`${what} copied`),
      () => toast.error("Could not copy. Select and copy it manually."),
    );

  async function approve(q: AdminCourseQuizItem) {
    setBusy(true);
    try {
      await api.admin.approveCourseItem(p.token, p.courseId, "quiz", q.id);
      toast.success("Published");
      await p.onChanged();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  async function bulkAccess(isFree: boolean) {
    setBusy(true);
    try {
      const payload = [...selected].map((id) => ({ item_id: id, item_type: "quiz" }));
      const res = await api.admin.bulkSetCourseItemAccess(p.token, p.courseId, payload, isFree);
      toast.success(res.message || `${res.count} updated`);
      setSelected(new Set());
      await p.onChanged();
    } catch (e) {
      toast.error(mapApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  const actions: RowAction<AdminCourseQuizItem>[] = [
    { key: "preview", label: "Preview as student", icon: <Eye size={14} />, group: 0, quick: true, onSelect: p.onPreview, hidden: (q) => !q.studentUrl },
    { key: "edit", label: "Edit details", icon: <Edit2 size={14} />, group: 0, quick: true, onSelect: p.onEdit },
    { key: "raw", label: "View questions", icon: <FileText size={14} />, group: 0, onSelect: p.onRaw },
    { key: "vis", label: "Hide / Show", icon: <EyeOff size={14} />, group: 1, onSelect: (q) => void p.onToggleVisibility(q), hidden: (q) => !q.isPublished },
    { key: "access", label: "Switch Free / Premium", icon: <Lock size={14} />, group: 1, onSelect: (q) => void p.onToggleAccess(q), hidden: () => p.isCoursePremium },
    { key: "link", label: "Copy student link", icon: <Link2 size={14} />, group: 2, onSelect: (q) => copy(`${window.location.origin}${q.studentUrl}`, "Link"), hidden: (q) => !q.studentUrl },
    { key: "id", label: "Copy ID", icon: <Hash size={14} />, group: 2, onSelect: (q) => copy(q.id, "ID") },
    { key: "delete", label: "Delete", icon: <Trash2 size={14} />, group: 3, danger: true, onSelect: p.onDelete },
  ];

  const columns: Column<AdminCourseQuizItem>[] = [
    { key: "status", header: "Status", className: "w-28 shrink-0", cell: (q) => { const s = statusOf(q); return <Badge variant={s.variant}>{s.label}</Badge>; } },
    {
      key: "access", header: "Access", className: "w-24 shrink-0", desktopOnly: true,
      cell: (q) => (p.isCoursePremium || q.isFree === false ? <Badge variant="warning"><Lock size={11} />Premium</Badge> : <Badge variant="info"><Unlock size={11} />Free</Badge>),
    },
    { key: "date", header: "Updated", className: "w-28 shrink-0", desktopOnly: true, sortValue: (q) => Date.parse(q.date) || 0, cell: (q) => <span className="text-muted-foreground">{q.date}</span> },
  ];

  const bulkActions: BulkAction[] = p.isCoursePremium
    ? []
    : [
        { key: "free", label: "Set free", icon: <Unlock size={14} />, onClick: () => void bulkAccess(true) },
        { key: "prem", label: "Set premium", icon: <Lock size={14} />, onClick: () => void bulkAccess(false) },
      ];

  return (
    <div className="space-y-3">
      <FilterBar list={list} filters={filters} searchLabel="Search quizzes" sortOptions={[{ key: "date", label: "Newest first", dir: "desc" }, { key: "date", label: "Oldest first", dir: "asc" }]} />
      <AdminDataTable<AdminCourseQuizItem>
        rows={filtered}
        rowId={(q) => q.id}
        noun="quizzes"
        columns={columns}
        actions={actions}
        list={list}
        selected={selected}
        onSelectedChange={setSelected}
        onOpen={p.onEdit}
        primary={(q) => (!q.isPublished && !p.isUnassigned && !busy ? { label: "Publish", onClick: () => void approve(q) } : null)}
        titleCell={(q) => (
          <>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-warning/10 text-warning">
              <HelpCircle size={16} />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{q.title}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {q.topic || "General"} · {q.questionCount} questions · {q.difficulty}
              </span>
            </span>
          </>
        )}
        empty={{
          title: "No quizzes yet",
          message: "Add the first quiz for this course.",
          action: p.onAdd && !p.isUnassigned ? { label: "Add quiz", onClick: p.onAdd } : undefined,
        }}
      />
      {bulkActions.length > 0 && <BulkBar count={selected.size} noun="quizzes" actions={bulkActions} busy={busy} onClear={() => setSelected(new Set())} />}
    </div>
  );
}
